import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getAnniversariesForMonth } from "../../api/anniversaries";
import { createInitialsAvatar } from "../../utils/initialsAvatar";
import calendar from "../../assets/calendar.png";
import { buildAssetUrl } from "../../data/authStorage";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";

const ANNIVERSARY_CACHE_KEY = "cehr_work_anniversaries_v1";

const WorkAnniversariesPage = () => {
  const cachedData = useMemo(
    () =>
      readSessionCache(ANNIVERSARY_CACHE_KEY, {
        celebrants: [],
        currentMonth: new Date().getMonth() + 1,
        currentPage: 1,
        totalPages: 1,
        total: 0,
      }),
    [],
  );
  const [celebrants, setCelebrants] = useState(cachedData?.celebrants || []);
  const [loading, setLoading] = useState(!cachedData?.celebrants?.length);
  const [currentMonth, setCurrentMonth] = useState(
    cachedData?.currentMonth || new Date().getMonth() + 1,
  );
  const [currentPage, setCurrentPage] = useState(cachedData?.currentPage || 1);
  const [totalPages, setTotalPages] = useState(cachedData?.totalPages || 1);
  const [total, setTotal] = useState(cachedData?.total || 0);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const loadAnniversaries = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent && celebrants.length === 0) {
        setLoading(true);
      }

      try {
        const response = await getAnniversariesForMonth(currentMonth, {
          page: currentPage,
          limit: 8,
        });
        const nextCelebrants = response.celebrants || [];
        const nextTotalPages = response.pagination?.pages || 1;
        const nextTotal = response.pagination?.total || 0;

        setCelebrants(nextCelebrants);
        setTotalPages(nextTotalPages);
        setTotal(nextTotal);
        writeSessionCache(ANNIVERSARY_CACHE_KEY, {
          celebrants: nextCelebrants,
          currentMonth,
          currentPage,
          totalPages: nextTotalPages,
          total: nextTotal,
        });
      } catch (error) {
        toast.error(error.message || "Failed to load anniversaries");
      } finally {
        setLoading(false);
      }
    },
    [celebrants.length, currentMonth, currentPage],
  );

  useEffect(() => {
    loadAnniversaries({ silent: true });
  }, [currentMonth, currentPage, loadAnniversaries]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadAnniversaries({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadAnniversaries]);

  const resolveImage = (imagePath, name) => {
    if (!imagePath) return createInitialsAvatar(name);
    return buildAssetUrl(imagePath) || createInitialsAvatar(name);
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";

    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatCelebrationDate = (month, day) => {
    if (!day) {
      return "-";
    }

    const monthName =
      monthNames[month - 1] || monthNames[new Date().getMonth()];
    return `${day}${getOrdinalSuffix(day)} ${monthName}`;
  };

  const isTodayCelebrant = (month, day) => {
    const today = new Date();
    return (
      today.getMonth() + 1 === month && Number(today.getDate()) === Number(day)
    );
  };

  const getAnniversaryDate = (celebrant) => {
    const dateOnlyMatch = `${celebrant?.dateJoined || ""}`.match(
      /^(\d{4})-(\d{2})-(\d{2})/,
    );

    return dateOnlyMatch
      ? { month: Number(dateOnlyMatch[2]), day: Number(dateOnlyMatch[3]) }
      : { month: currentMonth, day: celebrant?.day };
  };

  const highlightedCelebrantIndex = celebrants.findIndex((celebrant) =>
    isTodayCelebrant(
      getAnniversaryDate(celebrant).month,
      getAnniversaryDate(celebrant).day,
    ),
  );

  return (
    <div className="min-h-screen bg-[#f2f2f2] p-6 font-Nunito">
      <h1 className="mb-4 text-[26px] font-semibold text-[#1C1C1C]">
        Work anniversary celebrants
      </h1>

      <section className="rounded-xl border border-[#E7E8EE] bg-[#F7F8FC] p-5">
        <div className="mb-5 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <h2 className="text-[24px] font-bold text-[#101828]">
            🎊 Work Anniversary Celebrants - {monthNames[currentMonth - 1]}
          </h2>

          <div className="relative">
            <img
              src={calendar}
              alt="calendar"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-70"
            />

            <select
              value={currentMonth}
              onChange={(event) => {
                setCurrentMonth(Number(event.target.value));
                setCurrentPage(1);
              }}
              className="h-10 min-w-[170px] rounded-xl border border-[#D0D5DD] bg-white pl-9 pr-8 text-sm text-[#344054] outline-none"
            >
              {monthNames.map((name, index) => (
                <option
                  key={name}
                  value={index + 1}
                >
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="mb-6 text-sm text-[#667085]">
          {total} work anniversary celebrant{total === 1 ? "" : "s"} in{" "}
          {monthNames[currentMonth - 1]}
        </p>

        {loading ? (
          <FetchLoadingState message="Loading work anniversaries..." className="min-h-[360px] rounded-xl bg-white" />
        ) : celebrants.length === 0 ? (
          <div className="rounded-xl bg-white p-12 text-center">
            <h3 className="mb-2 text-xl font-semibold text-gray-800">
              No work anniversaries this month
            </h3>
            <p className="text-gray-600">Try selecting another month.</p>
          </div>
        ) : (
          <>
            <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {celebrants.map((celebrant, index) => {
                const isHighlighted = index === highlightedCelebrantIndex;
                const anniversaryDate = getAnniversaryDate(celebrant);
                const fullName =
                  `${celebrant.firstName || ""} ${celebrant.lastName || ""}`.trim() ||
                  "Unnamed Staff";
                const role = celebrant.positionName || "Staff";

                return (
                  <div
                    key={celebrant.id}
                    className={`relative h-[277px] rounded-md px-5 py-6 text-center transition ${
                      isHighlighted
                        ? "bg-gradient-to-b from-[#5608D4] to-[#384295] text-white shadow-lg"
                        : "border border-[#EEF0F6] bg-[#ECEEF6] text-[#0C111D]"
                    }`}
                  >
                    <span className="absolute right-4 top-3 text-base">🎉</span>

                    <div className="mb-4 flex justify-center">
                      <img
                        src={resolveImage(celebrant.profilePhotoUrl, celebrant.fullName)}
                        alt={fullName}
                        className="h-[86px] w-[86px] rounded-full border-2 border-[#2B52E6] object-cover"
                      />
                    </div>

                    <h3 className="mt-3 text-[18px] font-semibold leading-6">
                      {fullName}
                    </h3>
                    <p
                      className={`mt-1 text-sm ${isHighlighted ? "text-[#E4E7EC]" : "text-[#344054]"}`}
                    >
                      {role}
                    </p>

                    <p
                      className={`mt-11 text-sm font-medium ${isHighlighted ? "text-white" : "text-[#101828]"}`}
                    >
                      {isHighlighted
                        ? "Today's celebrant"
                        : formatCelebrationDate(
                          anniversaryDate.month,
                          anniversaryDate.day,
                        )}
                    </p>

                    {!isHighlighted && celebrant.yearsOfService ? (
                      <p className="mt-1 text-xs text-[#667085]">
                        {celebrant.yearsOfService} year
                        {celebrant.yearsOfService === 1 ? "" : "s"} of service
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
                >
                  ←
                </button>
                <div className="px-4 py-2 bg-white border border-gray-300 rounded-lg">
                  Page {currentPage} of {totalPages}
                </div>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
                >
                  →
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default WorkAnniversariesPage;
